import React from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";
import { css as cssMacro } from "styled-components/macro"; //eslint-disable-line

const Container = tw.div`relative -mx-8 -mt-8 font-work_sans`;
const TwoColumn = tw.div`flex flex-col lg:flex-row bg-primary-400 text-gray-900 min-h-screen`;

/* 手机端填满 + 垂直居中；PC端严格各占1/2 */
const LeftColumn = tw.div`px-8 xl:pl-10 pt-6 pb-10 lg:w-1/2 flex flex-col justify-center min-h-[50vh] lg:min-h-0`;

/* 右侧：移动端为整块背景；PC为居中的圆角正方形盒子 */
const RightColumn = styled.div(({ $bg }) => css`
  ${tw`w-full`}
  position: relative;
  flex: 1 1 auto;
  min-height: 50vh;
  background-color: #fdd96e;
  background-image: url("${$bg}");
  background-size: 105%;
  background-position: center;

  @media (min-width: 1024px) {
    flex: none;
    width: 50%;
    min-height: auto;
    /* PC 改为容器居中摆放方形图片，去掉整块背景图 */
    background-image: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`);

/* PC端显示的圆角正方形图片框（宽度=右侧1/2的84%） */
const ImageBox = styled.div(({ $bg }) => css`
  display: none;

  @media (min-width: 1024px) {
    display: block;
    width: 84%;
    aspect-ratio: 1 / 1;
    border-radius: 28px;
    overflow: hidden;
    background-image: url("${$bg}");
    background-size: 105%;
    background-position: center;
  }
`);

/* 内容区 */
const Content = tw.div`mt-2 lg:mt-4 mb-6 flex flex-col items-start text-left`;

/* 标题更紧 */
const Heading = styled.h1`
  ${tw`text-2xl sm:text-4xl md:text-5xl lg:text-5xl tracking-tight`}
  line-height: 1;
    font-size: 3rem;
  @media (min-width: 768px) { line-height: 0.98; }
`;

/* 描述文字更大一些 */
const Paragraph = styled.p`
  ${tw`my-6 lg:my-12 leading-snug`}
  max-width: 48rem;
  font-size: clamp(1.125rem, 1.05rem + 0.6vw, 1.375rem); /* 比原来更大 */
`;

/* 下载按钮 */
const StoreRow = styled.div`
  ${tw`mt-6 lg:mt-8 flex gap-3`}
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }
`;

const StoreBtn = styled.a`
  ${tw`select-none no-underline`}
  --badge-ratio: 3.4; /* 宽:高，App/Play 官方徽章大约 3.3~3.5 */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60%;
  max-width: 360px;
  padding: 0;
  background: transparent;
  border: 0;
  transition: transform .18s ease, opacity .18s ease;
  will-change: transform;

  &:active { transform: translateY(0); }

  img {
    width: 100%;
    height: auto;
  }

  @media (min-width: 640px) {
    width: auto;
    max-width: none;
    display: inline-flex;

    img {
      --badge-w: clamp(160px, 28vw, 220px);
      width: var(--badge-w);
      height: calc(var(--badge-w) / var(--badge-ratio));
    }
  }
`;

/* Rewards 文本链接（独立一行） */
const RewardsRow = tw.div`mt-6 lg:mt-6`;
const RewardsBtn = styled.a`
    ${tw`inline-flex items-center no-underline`}
    font-family: inherit;
    color: #14133B;
    font-weight: 600;
    gap: 8px;
    padding-bottom: 3px;
    position: relative;
    transition: color .2s ease, transform .2s ease;

    &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 1.5px;
        background-color: rgba(20,19,59,0.55);
        transition: background-color .2s ease, height .2s ease;
    }

    &:hover {
        color: #0f0e3a;
        transform: translateY(-1px);
    }

    &:hover::after {
        background-color: #0f0e3a;
        height: 2px;
    }

    .arrow {
        transition: transform .2s ease;
    }
    &:hover .arrow { transform: translateX(3px); }
`;

export default ({
                    heading = (
                        <>
                            <span>YOUR BOWL,</span>
                            <br />
                            <span>JUST A TAP</span>
                            <br /><span>AWAY.</span>
                        </>
                    ),
                    description =
                    "Skip the line and order ahead with the Grain App. Every order earns points you can redeem for rewards. It’s the Healthy. Hearty. Honest. way to eat.",
                    appStoreUrl = "https://apps.apple.com/us/app/grain-mediterranean/id6752673762",
                    playStoreUrl = "https://play.google.com/store/apps/details?id=au.com.redcat.grainmediterranean",
                    rewardsUrl = "https://grainmediterranean.redcatcloud.com.au/universal/register",
                    rightImage = "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/1981759887209_.pic.jpg",
                }) => {
    return (
        <Container>
            <TwoColumn>
                <LeftColumn>
                    <Content>
                        <Heading>{heading}</Heading>
                        <Paragraph>{description}</Paragraph>

                        {/* 两个下载按钮 */}
                        <StoreRow>
                            <StoreBtn href={appStoreUrl} target="_blank" rel="noopener" aria-label="Download on the App Store">
                                <img
                                    src="https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/5821761243982_.pic_hd.jpg"
                                    alt="Download on the App Store"
                                />
                            </StoreBtn>

                            <StoreBtn href={playStoreUrl} target="_blank" rel="noopener" aria-label="Get it on Google Play">
                                <img
                                    src="https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/5831761243983_.pic_hd.jpg"
                                    alt="Get it on Google Play"
                                />
                            </StoreBtn>
                        </StoreRow>

                        {/* Rewards 按钮 */}
                        <RewardsRow>
                            <RewardsBtn href={rewardsUrl}>
                                Learn more about Grain Rewards <span className="arrow">→</span>
                            </RewardsBtn>
                        </RewardsRow>
                    </Content>
                </LeftColumn>

                <RightColumn $bg={rightImage}>
                    {/* PC端显示的圆角正方形图片框（宽度=右侧1/2的80%） */}
                    <ImageBox $bg={rightImage} aria-hidden="true" />
                </RightColumn>
            </TwoColumn>
        </Container>
    );
};
